import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime } from '@/lib/format';
import { Wifi, WifiOff, AlertTriangle, HelpCircle, Loader2 } from 'lucide-react';
import { useLocationPiHealth } from '@/api/hooks/useAdminUnits';
import type { PiConnectionHealthResponse } from '@/api/types';

/**
 * No client-side grace lives here anymore. The server computes the effective state (CONNECTED /
 * RECONNECTING / DISCONNECTED / NEVER_CONNECTED) so the UI and the disconnect alert apply one rule and
 * neither depends on the operator's browser clock. This card just renders whatever state the server sends.
 */

/** The declared device list — what the platform knows the Pi can reach; makes PI_DEVICE_UNKNOWN diagnosable. */
function DeviceList({ devices }: { devices?: string[] | null }) {
  const { t } = useTranslation();
  if (!devices || devices.length === 0) {
    return <p className="text-xs text-muted-foreground">{t('admin.pi_no_devices')}</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {devices.map((d) => (
        <Badge key={d} variant="secondary" className="text-xs font-mono">
          {d}
        </Badge>
      ))}
    </div>
  );
}

/**
 * Location-level Pi transport health, shown on the units page when a specific location is selected. Renders
 * the server's effective state — four visually distinct states (CONNECTED / RECONNECTING / DISCONNECTED /
 * NEVER_CONNECTED) so a Pi that died at 4pm is visible at 4pm, a routine blip shows a muted "reconnecting…"
 * rather than the red alarm, and NEVER_CONNECTED (a setup task) never looks like DISCONNECTED (a facility
 * that fails tonight). The grace is on the server; there is no client-side grace math here. Non-blocking:
 * its own query + loading state, so a slow pi-connection call never slows the units list.
 */
export default function PiHealthCard({ locationId }: { locationId: string }) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useLocationPiHealth(locationId);

  if (isLoading) {
    return <Skeleton className="h-24" />;
  }
  // A failed pi-connection read must never take down the page — degrade quietly.
  if (isError || !data) {
    return null;
  }

  return <PiHealthCardBody health={data} lang={i18n.language} t={t} />;
}

/** Split out (pure over the health payload) so tests can drive all three states without the network. */
export function PiHealthCardBody({
  health,
  lang,
  t,
}: {
  health: PiConnectionHealthResponse;
  lang: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const lastSeen = health.lastSeenAt ? formatDateTime(health.lastSeenAt, lang) : '—';

  if (health.state === 'CONNECTED') {
    return (
      <Card className="border-l-4 border-l-green-500">
        <CardContent className="p-4">
          <div className="mb-2 flex items-center gap-2">
            <Wifi className="h-4 w-4 text-green-600" />
            <h3 className="text-sm font-semibold">{t('admin.pi_connected')}</h3>
            {health.piVersion && (
              <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                {t('admin.pi_version', { version: health.piVersion })}
              </Badge>
            )}
          </div>
          <div className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
            <span>
              {t('admin.pi_connected_since')}:{' '}
              {health.connectedSince ? formatDateTime(health.connectedSince, lang) : '—'}
            </span>
            <span>
              {t('admin.pi_last_seen')}: {lastSeen}
            </span>
          </div>
          <div className="mt-3">
            <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('admin.pi_declared_devices')}
            </h4>
            <DeviceList devices={health.devices} />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (health.state === 'NEVER_CONNECTED') {
    // A SETUP task — deliberately muted, NOT the red alarm DISCONNECTED gets.
    return (
      <Card className="border-l-4 border-l-slate-400">
        <CardContent className="p-4">
          <div className="mb-1 flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-700">{t('admin.pi_never_connected')}</h3>
          </div>
          <p className="text-xs text-muted-foreground">{t('admin.pi_never_connected_detail')}</p>
        </CardContent>
      </Card>
    );
  }

  if (health.state === 'RECONNECTING') {
    // Within the server's grace: a routine reconnect in progress — muted "reconnecting…", never the alarm.
    return (
      <Card className="border-l-4 border-l-amber-400">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
            <h3 className="text-sm font-semibold text-amber-700">{t('admin.pi_reconnecting')}</h3>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('admin.pi_last_seen')}: {lastSeen}
          </p>
        </CardContent>
      </Card>
    );
  }

  // DISCONNECTED (server, past grace): a genuine outage — prominent red alarm, distinct from NEVER_CONNECTED.
  return (
    <Card className="border-2 border-red-500 bg-red-50">
      <CardContent className="p-4">
        <div className="mb-1 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <WifiOff className="h-4 w-4 text-red-600" />
          <h3 className="text-sm font-bold text-red-800">
            {t('admin.pi_disconnected_since', { time: lastSeen })}
          </h3>
        </div>
        <p className="text-xs text-red-700">
          {t('admin.pi_disconnected_detail')} · {t('admin.pi_last_seen')}: {lastSeen}
        </p>
        {health.devices && health.devices.length > 0 && (
          <div className="mt-3">
            <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-red-700/70">
              {t('admin.pi_declared_devices')}
            </h4>
            <DeviceList devices={health.devices} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
